import { NavigateFunction } from 'react-router-dom'

import { Map } from 'immutable'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'

import { isToolkitTypeCenterOfExcellence } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/utils'
import { TREATMENT_OPTIONS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { getNewSearchData } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import {
  SearcherInfo,
  SearcherType,
  TreatmentRecommendations,
} from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'

import { createSearchAndFetchTreatmentRecommendations, setTreatmentRecommendations } from './triageActions'
import {
  getIsSearchToolkitAware,
  getIsTriageSearchInFlight,
  getProgramPreference,
  getToolkitSubTopic,
} from './triageSelectors'
import { setBannerAndErrorMessage } from '../../../common/components/banner/banner-actions'
import { TREATMENT_RECOMMENDATIONS_ROUTE } from '../../../common/constants/routingConstants'
import {
  getCustomerProductType,
  getCustomerPropertiesMap,
  getIsCoachingEnabled,
  getProgramCoverageBreakdown,
  getShouldSeeCareAdvocateRequestForm,
} from '../../../data/customer/customerSelectors'
import {
  getId,
  getIsUserInternational,
  getUser,
  getUserCountryName,
  getUserGenderString,
  getUserPreferredAppointmentTypes,
} from '../../../data/user/userSelectors'
import { getHealthPlan } from '../../healthPlan/data/healthPlanSelectors'
import { getOnboardToolkitType } from '../../onboard/data/onboardSelectors'

const defaultICASTreatmentRecommendations = {
  primaryTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE],
  secondaryTreatmentOptions: [],
  eligibleTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE],
} satisfies TreatmentRecommendations

const defaultICASChildTreatmentRecommendations = {
  primaryTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE],
  secondaryTreatmentOptions: [],
  eligibleTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE],
} satisfies TreatmentRecommendations

export const createSearchAndNavigate = ({
  navigate,
  searcher,
}: {
  navigate: NavigateFunction
  searcher: Pick<SearcherInfo, 'dob' | 'type' | 'childLyraId'>
}) => {
  return async (
    dispatch: ThunkDispatch<Map<string, any>, undefined, AnyAction>,
    getState: () => Map<string, any>,
  ): Promise<void> => {
    const state = getState()
    // Prevent duplicate searches from repeated clicks while a search is already being created
    if (getIsTriageSearchInFlight(state)) {
      return
    }
    const user = getUser(state)
    const patient = getHealthPlan(state)
    const toolkitType = getOnboardToolkitType(state)
    const toolkitSubTopic = getToolkitSubTopic(state)
    const countryName = getUserCountryName(state)
    const newSearchData = getNewSearchData({
      userId: getId(state),
      dob: searcher.dob,
      gender: getUserGenderString(state) || 'unknown',
      employer: user?.getIn(['customers', 'name']),
      type: searcher.type,
      childUserId: searcher.childLyraId,
      productType: getCustomerProductType(state),
      toolkitType,
      toolkitSubTopic,
      eligibilityType: patient?.get('eligibilityType') ?? user?.get('eligibilityType'),
      countryName,
    })
    // NOTE - this function is only used in oUS flows, so the default treatment recs are set to ICAS options.
    const defaultTreatmentRecommendations =
      searcher.type === SearcherType.ADULT
        ? defaultICASTreatmentRecommendations
        : defaultICASChildTreatmentRecommendations

    if (!newSearchData.lyra_id) {
      dispatch(
        setBannerAndErrorMessage(new Error('Cannot start the search because member information is missing.'), true),
      )
      dispatch(setTreatmentRecommendations(defaultTreatmentRecommendations))
      navigate(TREATMENT_RECOMMENDATIONS_ROUTE)
      return
    }

    const programPreference = getProgramPreference(state)
    const useProgramPreference =
      programPreference &&
      !isToolkitTypeCenterOfExcellence(toolkitType, toolkitSubTopic) &&
      !getIsSearchToolkitAware(state)

    try {
      await dispatch(
        createSearchAndFetchTreatmentRecommendations({
          newSearchData: {
            ...newSearchData,
            body: {
              ...newSearchData.body,
              ...(useProgramPreference ? { programPreference } : {}),
            },
          },
          treatmentRecommendationsData: {
            customerProperties: getCustomerPropertiesMap(state).toJS(),
            currentProgramCoverageBreakdown: getProgramCoverageBreakdown(state).toJS(),
            countryName: countryName || 'N/A',
            healthPlanEligibilityName: patient?.get('healthPlanName'),
            isClientBHBEligible: patient?.get('isEligibleForHealthPlanExtension'),
            isInternational: getIsUserInternational(state),
            preferredAppointmentTypes: getUserPreferredAppointmentTypes(state).toJS(),
          },
          treatmentRecommendationsParams: {
            stressManagement: getIsCoachingEnabled(state),
            shouldSeeCareAdvocateRequestForm: getShouldSeeCareAdvocateRequestForm(state),
          },
        }),
      )
    } catch {
      dispatch(setTreatmentRecommendations(defaultTreatmentRecommendations))
    }
    navigate(TREATMENT_RECOMMENDATIONS_ROUTE)
  }
}
